import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FormulaEditor from '../components/lab/FormulaEditor';
import MethodBody from '../components/lab/MethodBody';
import PhaseKey from '../components/lab/PhaseKey';
import MethodForm from '../components/lab/MethodForm';
import VariantForm from '../components/lab/VariantForm';
import { useConfirm } from '../components/ConfirmDialog';
import { formatPct, isBalanced, sumPct } from '../lib/lab';
import {
  deleteVariant,
  effectiveMethod,
  getLabFormula,
  listBatches,
  listMethods,
  listProductTypes,
  setFlexIngredient,
  setFormulaMethod,
} from '../lib/labData';
import { createVersion, updateFamily } from '../lib/formulas';
import { groupByPhase } from '../lib/formulaModel';
import './Lab.css';

const DATE = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

// One formula, full page — no right rail. The rail duplicated the list and
// then showed three near-empty panels; a formula deserves the width.
export default function FormulaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [formula, setFormula] = useState(null);
  const [methods, setMethods] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [variantForm, setVariantForm] = useState(null);
  const [methodForm, setMethodForm] = useState(null);
  const [attaching, setAttaching] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [f, m, t, b] = await Promise.all([
        getLabFormula(id),
        listMethods(),
        listProductTypes(),
        listBatches(),
      ]);
      setFormula(f);
      setMethods(m);
      setProductTypes(t);
      setBatches(b.filter((batch) => batch.formula?.family_id === id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="formula-loading">Loading…</p>;
  if (error) return <p className="formula-error">Couldn't load this formula: {error}</p>;
  if (!formula) return <p className="formula-error">That formula no longer exists.</p>;

  const version = formula.currentVersion;
  const lines = version?.formula_ingredients || [];
  const total = sumPct(lines);
  const balanced = isBalanced(lines);
  const method = effectiveMethod(formula, methods);
  const inherited = !formula.method_id && method;

  const handleSaveVersion = async (versionFields, ingredients, changeNote, familyExtras) => {
    await createVersion(formula.id, versionFields, ingredients, changeNote);
    // The name and product type belong to the family, not the version, so
    // they're saved separately — otherwise renaming a formula in the editor
    // silently does nothing.
    const familyChanges = {};
    if (versionFields.product_name && versionFields.product_name !== formula.name) {
      familyChanges.name = versionFields.product_name;
    }
    if (familyExtras?.product_type_id && familyExtras.product_type_id !== formula.product_type_id) {
      familyChanges.product_type_id = familyExtras.product_type_id;
      familyChanges.product_type = versionFields.product_type;
    }
    if (Object.keys(familyChanges).length) await updateFamily(formula.id, familyChanges);
    setEditing(false);
    await load();
  };

  const handleDeleteVariant = async (variant) => {
    if (
      !(await confirm({
        title: `Delete ${variant.name}?`,
        body: 'Batches already made from it keep their own frozen copy of what they used, so their records are unaffected.',
      }))
    )
      return;
    await deleteVariant(variant.id);
    await load();
  };

  if (editing) {
    return (
      <>
        <div className="lab-crumb">
          <Link to="/lab">← Lab</Link>
        </div>
        <section className="panel">
          <h3 className="panel-title">Edit {formula.name}</h3>
          <FormulaEditor
            formula={formula}
            version={version}
            productTypes={productTypes}
            requireChangeNote
            hasVariants={(formula.variants || []).length > 0}
            onSubmit={handleSaveVersion}
            onCancel={() => setEditing(false)}
          />
        </section>
      </>
    );
  }

  return (
    <>
      <div className="lab-crumb">
        <Link to="/lab">← Lab</Link>
      </div>

      <header className="lab-detail-head">
        <div>
          <h1 className="lab-detail-title">{formula.name}</h1>
          <p className="lab-detail-sub">
            {formula.product_type?.name || formula.product_type}
            {version ? ` · v${version.version_number}` : ' · no version yet'}
          </p>
        </div>
        {/* The point of the page. Nothing else on it is a primary button. */}
        <button className="primary-action" onClick={() => navigate(`/production?make=${formula.id}`)}>
          Make a batch
        </button>
      </header>

      {/* ---------------------------------------------------- composition */}
      <section className="panel lab-card">
        <div className="panel-head">
          <span className="panel-label">Composition</span>
          <button className="link-button" onClick={() => setEditing(true)}>
            Edit formula
          </button>
        </div>

        {lines.length === 0 ? (
          <p className="formula-muted">No ingredients yet.</p>
        ) : (
          <table className="lab-table">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th className="numeric">%</th>
              </tr>
            </thead>
            {groupByPhase(lines).map(([phase, rows]) => (
              <tbody key={phase}>
                <tr className="phase-row">
                  <td colSpan={2}>{phase}</td>
                </tr>
                {rows.map((line) => (
                  <tr key={line.id}>
                    <td>
                      {line.ingredient_name}
                      {/* Both sides can be null — an unlinked line and a
                          formula with no flex nominated — and null === null
                          would tag every row. */}
                      {version?.flex_ingredient_id &&
                        line.ingredient_id === version.flex_ingredient_id && (
                        <span className="flex-tag">
                          <i className="flex-dot" /> flex
                        </span>
                      )}
                    </td>
                    <td className="numeric">{formatPct(line.percentage)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
            <tfoot>
              <tr className={balanced ? 'lab-total ok' : 'lab-total off'}>
                <td>Total</td>
                <td className="numeric">
                  {formatPct(total)}
                  {!balanced && (
                    <em>
                      {' '}
                      {total > 100 ? `${formatPct(total - 100)} over` : `${formatPct(100 - total)} short`}
                    </em>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        )}

        {/* Nominating the flex line edits the version in place rather than
            creating a new one. Which line absorbs a variant's difference is
            not a change to what's in the product — and batches snapshot
            their own resolved percentages anyway, so nothing already made
            can be affected by it. Forcing a version for this would pollute
            the change history exactly the way storing the method there
            would have. */}
        {lines.length > 0 && <PhaseKey />}

        {lines.length > 0 && (
          <div className="lab-flex-picker">
            <label>
              Flex ingredient
              <select
                value={version?.flex_ingredient_id || ''}
                onChange={async (e) => {
                  await setFlexIngredient(version.id, e.target.value || null);
                  await load();
                }}
              >
                <option value="">None</option>
                {lines
                  .filter((l) => l.ingredient_id)
                  .map((l) => (
                    <option key={l.ingredient_id} value={l.ingredient_id}>
                      {l.ingredient_name}
                    </option>
                  ))}
              </select>
            </label>
            <em>
              {version?.flex_ingredient_id
                ? 'Absorbs the difference when a variant changes another line, so every variant still totals 100%.'
                : 'Pick the line that should absorb variant differences. Required before this formula can have variants.'}
            </em>
          </div>
        )}
      </section>

      {/* -------------------------------------------------------- variants */}
      <section className="panel lab-card">
        <div className="panel-head">
          <span className="panel-label">Variants</span>
          {version && (
            <button className="link-button" onClick={() => setVariantForm({})}>
              + Add variant
            </button>
          )}
        </div>

        {(formula.variants || []).length === 0 ? (
          <p className="formula-muted">
            No variants. Add one if a scent needs a different fragrance level.
          </p>
        ) : (
          <table className="lab-table">
            <thead>
              <tr>
                <th>Variant</th>
                <th>What differs</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {formula.variants.map((variant) => (
                <tr key={variant.id}>
                  <td>{variant.name}</td>
                  <td className="lab-variant-diff">
                    {(variant.overrides || [])
                      .map((o) => {
                        const line = lines.find((l) => l.ingredient_id === o.ingredient_id);
                        return `${line?.ingredient_name || 'Unknown'} ${formatPct(o.pct)}`;
                      })
                      .join(' · ') || '—'}
                  </td>
                  <td>
                    <span className={'lab-pill ' + variant.status}>{variant.status}</span>
                  </td>
                  <td className="lab-row-actions">
                    <button className="link-button" onClick={() => setVariantForm(variant)}>
                      Edit
                    </button>
                    <button className="link-button" onClick={() => handleDeleteVariant(variant)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ---------------------------------------------------------- method */}
      <section className="panel lab-card">
        <div className="panel-head">
          <span className="panel-label">
            Method{inherited && <em className="lab-inherited"> · from {formula.product_type?.name}</em>}
          </span>
          <span className="lab-row-actions">
            <button className="link-button" onClick={() => setAttaching(true)}>
              {method ? 'Change method' : 'Attach a method'}
            </button>
            {method && (
              <button className="link-button" onClick={() => setMethodForm(method)}>
                Edit method
              </button>
            )}
          </span>
        </div>

        {method ? (
          <>
            <p className="lab-method-name">{method.name}</p>
            <MethodBody method={method} />
          </>
        ) : (
          <div className="lab-empty-inline">
            <p>No method attached. Batch sheets for this formula will have no steps.</p>
            <button className="ghost-button" onClick={() => setAttaching(true)}>
              Attach a method
            </button>
          </div>
        )}

        {attaching && (
          <div className="lab-attach">
            <select
              defaultValue={formula.method_id || ''}
              onChange={async (e) => {
                await setFormulaMethod(formula.id, e.target.value || null);
                setAttaching(false);
                await load();
              }}
            >
              <option value="">No method</option>
              {methods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <button className="ghost-button" onClick={() => setAttaching(false)}>
              Cancel
            </button>
            <button className="link-button" onClick={() => setMethodForm({})}>
              + New method
            </button>
          </div>
        )}
      </section>

      {/* --------------------------------------------------------- history */}
      <section className="panel lab-card">
        <div className="panel-head">
          <span className="panel-label">History</span>
        </div>
        <div className="lab-history">
          <div>
            <h4>Versions</h4>
            <ul className="lab-history-list">
              {formula.versions.slice(0, 5).map((v) => (
                <li key={v.id}>
                  <strong>v{v.version_number}</strong>
                  <span>{v.change_note || 'No note'}</span>
                  <em>{DATE.format(new Date(v.created_at))}</em>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Recent batches</h4>
            {batches.length === 0 ? (
              <p className="formula-muted">
                No batches yet. When you make something, it'll show up here with what you actually
                got out of it.
              </p>
            ) : (
              <ul className="lab-history-list">
                {batches.slice(0, 5).map((b) => (
                  <li key={b.id}>
                    <Link to={`/production/batches/${b.id}`}>{b.batch_code || 'Batch'}</Link>
                    <span>{b.variant?.name || `v${b.formula?.version_number}`}</span>
                    <em>{b.actual_yield_units != null ? `${b.actual_yield_units} units` : b.status}</em>
                  </li>
                ))}
              </ul>
            )}
            {batches.length > 5 && <Link className="link-button" to="/production">View all</Link>}
          </div>
        </div>
      </section>

      {variantForm && (
        <VariantForm
          formula={formula}
          version={version}
          variant={variantForm.id ? variantForm : null}
          onClose={() => setVariantForm(null)}
          onSaved={load}
        />
      )}

      {methodForm && (
        <MethodForm
          method={methodForm.id ? methodForm : null}
          productTypes={productTypes}
          onClose={() => setMethodForm(null)}
          onSaved={async () => {
            const fresh = await listMethods();
            setMethods(fresh);
            // A method created from here is attached straight away —
            // otherwise you write it and it goes nowhere.
            if (!methodForm.id && !formula.method_id) {
              const created = fresh.find((m) => !methods.some((old) => old.id === m.id));
              if (created) await setFormulaMethod(formula.id, created.id);
            }
            await load();
          }}
        />
      )}
    </>
  );
}
